import { Link } from "react-router-dom";
import { IconPencil } from "../../../components/icons";
import { useAuth } from "../../../contexts/AuthProvider";
import {
  formListCreatedAt,
  isFormListItemClosed,
} from "../../../lib/forms/formMeta";
import {
  formEditorUrl,
  formListAnswersCount,
  formPublicYandexUrl,
  isYandexForm,
} from "../../../lib/forms/formSource";
import type { FormListItemDto } from "../../../types/formList";
import { FormPublicLinkShare } from "../_components/FormPublicLinkShare";

function formatDate(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });
}

function FormStatusDot({ closed }: { closed: boolean }) {
  const label = closed ? "Закрыто" : "Опубликовано";
  return (
    <span
      title={label}
      aria-label={label}
      className={[
        "inline-block h-1.5 w-1.5 shrink-0 rounded-full",
        closed ? "bg-slate-300" : "bg-emerald-500",
      ].join(" ")}
    />
  );
}

interface FormListRowProps {
  form: FormListItemDto;
  onCopyError?: (message: string) => void;
  showSelectionColumn?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}

export function FormListRow({
  form,
  onCopyError,
  showSelectionColumn = false,
  selectable = false,
  selected = false,
  onToggleSelect,
}: FormListRowProps) {
  const closed = isFormListItemClosed(form);
  const createdAt = formatDate(formListCreatedAt(form));
  const answersCount = formListAnswersCount(form);
  const yandex = isYandexForm(form);
  const publicUrl = yandex ? formPublicYandexUrl(form) : null;
  const editorUrl = yandex ? formEditorUrl(form) : null;
  const { getOrganisationName } = useAuth();
  const organisationName = getOrganisationName(form.organisationId);
  const titleParts = [
    form.name,
    form.description?.trim() || null,
    organisationName,
    form.externalService?.name,
    !organisationName && form.organisationId == null && yandex
      ? "Без организации"
      : null,
  ].filter(Boolean);

  return (
    <tr className="group border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50">
      {showSelectionColumn ? (
        <td className="w-8 px-2 py-1.5 pl-3">
          {selectable ? (
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              aria-label={`Выбрать «${form.name}»`}
              className="h-3.5 w-3.5 rounded border-slate-300 text-brand-500 focus:ring-brand-500/30"
            />
          ) : null}
        </td>
      ) : null}
      <td className="min-w-0 py-1.5 pr-2">
        <div className="flex min-w-0 items-center gap-2">
          <FormStatusDot closed={closed} />
          <Link
            to={`/admin/forms/${form.id}/edit`}
            title={titleParts.join(" · ")}
            className="min-w-0 truncate font-onest text-sm font-medium text-ink hover:text-brand-700"
          >
            {form.name}
          </Link>
          {yandex ? (
            <span
              title="Яндекс Формы"
              className="shrink-0 font-onest text-[10px] font-semibold text-amber-700"
            >
              Я
            </span>
          ) : null}
          {form.isServiceForm ? (
            <span
              title={
                form.externalService?.name
                  ? `Внешний сервис: ${form.externalService.name}`
                  : "Форма внешнего сервиса"
              }
              className="shrink-0 rounded border border-cyan-200 bg-cyan-50 px-1.5 py-0.5 font-onest text-[10px] font-semibold text-cyan-800"
            >
              {form.externalService?.name || "Сервис"}
            </span>
          ) : null}
          {organisationName ? (
            <span
              title={organisationName}
              className="hidden min-w-0 max-w-[10rem] truncate font-onest text-[11px] text-slate-400 lg:inline"
            >
              {organisationName}
            </span>
          ) : null}
        </div>
      </td>
      <td className="w-36 px-2 py-1.5 text-right font-onest text-xs tabular-nums text-slate-500">
        {answersCount != null ? (
          <span title={`${answersCount} ответов`}>{answersCount}</span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td className="hidden w-24 whitespace-nowrap px-2 py-1.5 text-left font-onest text-xs text-slate-500 md:table-cell">
        {createdAt}
      </td>
      <td className="w-10 px-2 py-1.5 pr-3 text-right">
        <div className="flex items-center justify-end">
          {yandex && publicUrl ? (
            <FormPublicLinkShare
              url={publicUrl}
              popoverTitle="Ссылка на форму Яндекса"
              placement="top"
              onCopyError={onCopyError}
            />
          ) : yandex && editorUrl ? (
            <a
              href={editorUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Конструктор Яндекса"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
            >
              <IconPencil size={14} />
            </a>
          ) : (
            <FormPublicLinkShare
              formId={form.id}
              publicId={form.publicId}
              placement="top"
              onCopyError={onCopyError}
            />
          )}
        </div>
      </td>
    </tr>
  );
}
